import styled from 'styled-components';

import { Text } from '@/nova/components';
import {
  GRADIENTS,
  MOBILE,
  SHOWCASE_CORNER_GLOW,
  SHOWCASE_INK,
  SHOWCASE_INK_MUTED,
  SHOWCASE_VIDEO_BG,
} from '@/nova/theme/tokens';

export const Root = styled.section`
  position: relative;
  /* Contain the stage's internal layering (the active highlight's z-index, the corner
   *  glow) so it can't escape to the page level and paint over the sticky feature
   *  status bar above. The whole stage then stacks as one layer beneath that bar. */
  isolation: isolate;
  display: flex;
  /* Even 50/50 split: the highlights get half the width (room for the titles to breathe
   *  instead of ellipsizing) and the demo clip the other half. */
  gap: 40px;
  min-height: 360px;
  padding: 32px 24px;
  border-radius: 16px;
  /* Solid black cinematic surface (both themes) — the demo clip fills its half and
   *  letterboxes against it seamlessly. */
  background: ${SHOWCASE_VIDEO_BG};
  overflow: hidden;

  ${MOBILE} {
    flex-direction: column;
    gap: 10px;
    padding: 10px;
    min-height: 0;
  }
`;

export const LeftColumn = styled.div`
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  /* Top-aligned so an expanding card grows DOWNWARD instead of re-centering the
   *  whole group (which made it jump vertically). */
  justify-content: flex-start;
  gap: 24px;
  min-width: 0;

  ${MOBILE} {
    flex: 0 0 auto;
    width: 100%;
    gap: 10px;
  }
`;

// Two-tone ambient corner highlights, sitting on TOP of the whole stage + its content
// (high z-index, pointer-events off) so the black surface reads with some depth.
export const CornerGlow = styled.div`
  position: absolute;
  inset: 0;
  z-index: 30;
  pointer-events: none;
  background: ${SHOWCASE_CORNER_GLOW};
`;

export const Heading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const Eyebrow = styled(Text)`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  line-height: 1.1;
  /* Branded title gradient, same lilac->mauve->tan sweep as the modal + drill-in titles. */
  background: ${GRADIENTS.backHeaderTitle};
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
`;

export const HeadingTitle = styled(Text)`
  font-size: 24px;
  font-weight: 500;
  line-height: 1.2;
  color: ${SHOWCASE_INK};
`;

export const Stack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  width: 100%;

  ${MOBILE} {
    gap: 10px;
  }
`;

export const EmptyNote = styled(Text)`
  font-size: 14px;
  color: ${SHOWCASE_INK_MUTED};
`;

// Flex container that lets the demo-clip frame stretch to fill the right half (both
// dimensions) on desktop; on mobile the frame goes full-width with its own aspect.
export const Centre = styled.div`
  flex: 1 1 0;
  display: flex;
  min-width: 0;

  ${MOBILE} {
    flex: 0 0 auto;
    width: 100%;
  }
`;

// --- Centre fallback (when the active highlight has no / unplayable clip) --------
export const Fallback = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 24px;
  text-align: center;
`;

export const FallbackIcon = styled.img`
  width: 64px;
  height: 64px;
  object-fit: contain;
  opacity: 0.9;
`;

export const FallbackName = styled(Text)`
  font-size: 16px;
  font-weight: 500;
  color: ${SHOWCASE_INK};
`;

export const FallbackNote = styled(Text)`
  font-size: 12px;
  color: ${SHOWCASE_INK_MUTED};
`;
